import { IconButton, NewMenu } from '@common-components';
import { MoreVert, OpenInNew } from '@icons';
import { SubmissionStatus, SubmissionTerminationStatus } from 'enums';
import { useBoolean, useConditionallyRenderedModal, useOptionsMenu } from 'hooks';
import { useReFetchBoxItems } from 'hooks/api/box';
import { useMutateSalesDemo } from 'hooks/api/salesDemo';
import { useRefetchSubmissionEmails } from 'hooks/api/submissionEmail';
import { messages } from 'i18n';
import { InboundEmailMessageSearchItem, PartialSubmission } from 'types';
import { MenuItemProps } from 'components/menu';
import TerminateSubmissionDialog from 'broker/dialogs/TerminateSubmissionDialog';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useDownloadMenu } from './useDownloadMenu';

const websiteBaseUrl = process.env.REACT_APP_WEBSITE_BASE_URL;

type TerminationDialogProps = {
  targetStatus: SubmissionTerminationStatus;
};

export interface SubmissionHeaderMenuProps {
  submission: PartialSubmission;
  submissionCreationEmail?: InboundEmailMessageSearchItem;
}

export function SubmissionHeaderMenu({ submission, submissionCreationEmail }: SubmissionHeaderMenuProps) {
  const optionsMenuState = useOptionsMenu();
  const { downloadMenuItems, isDownloading } = useDownloadMenu({ submission, submissionCreationEmail });
  const { reFetchWorkspace } = useSubmissionsWorkspace();
  const { reFetchFileItems } = useReFetchBoxItems();
  const { reFetchSubmissionEmails } = useRefetchSubmissionEmails();
  const { clearSubmission } = useMutateSalesDemo();
  const [isClearingSubmissionData, { on: startClearingData, off: stopClearingData }] = useBoolean(false);

  const {
    openModal: openTerminationDialog,
    closeModal: closeTerminationDialog,
    modalState: terminationDialogState,
  } = useConditionallyRenderedModal<TerminationDialogProps>({});

  const onArchiveClick = () => {
    openTerminationDialog({ targetStatus: SubmissionStatus.Archived });
  };

  const onCloseSubmissionClick = () => {
    openTerminationDialog({ targetStatus: SubmissionStatus.Closed });
  };

  const getMenuItems = (): MenuItemProps[] => {
    const menuItems: MenuItemProps[] = [];
    const closeSubmissionMenuItem: MenuItemProps = {
      label: messages.submissionWorkspace.termination.closeMenuLabel,
      disabled: submission.status === SubmissionStatus.Closed,
      onClick: onCloseSubmissionClick,
    };
    const archiveMenuItem: MenuItemProps = {
      label: messages.submissionWorkspace.termination.archiveMenuLabel,
      disabled: submission.status === SubmissionStatus.Archived,
      onClick: onArchiveClick,
    };
    const downloadMenuItem: MenuItemProps = {
      label: messages.submissionWorkspace.downloadFilesAndEmails.menuLabel,
      subMenu: downloadMenuItems,
    };
    const clearSubmissionMenuItem: MenuItemProps = {
      label: messages.submissionWorkspace.clearSubmissionData.menuLabel,
      onClick: async () => {
        startClearingData();
        await clearSubmission.mutateAsync({ submissionId: submission.id });
        await reFetchWorkspace();
        await reFetchFileItems();
        await reFetchSubmissionEmails();
        stopClearingData();
      },
    };
    menuItems.push(closeSubmissionMenuItem);
    menuItems.push(archiveMenuItem);
    if (submission.priorSubmissionId) {
      menuItems.push({
        label: messages.submissionWorkspace.renewalSubmission.openPriorSubmissionMenuLabel,
        endAdornment: OpenInNew,
        onClick: () => {
          window.open(`${websiteBaseUrl}/broker/submission/${submission.priorSubmissionId}`, '_blank');
        },
      });
    }
    menuItems.push(downloadMenuItem);
    if (submission.isDemo) {
      menuItems.push(clearSubmissionMenuItem);
    }
    return menuItems;
  };

  return (
    <>
      <IconButton
        icon={MoreVert}
        onClick={optionsMenuState.openMenu}
        color="secondary"
        variant="text"
        loading={isDownloading || isClearingSubmissionData}
      />
      <NewMenu
        optionsMenuState={optionsMenuState}
        subMenuPlacement="left"
        menuItems={[
          {
            key: 'submission-header-menu',
            items: getMenuItems(),
          },
        ]}
      />
      {terminationDialogState.shouldRender && (
        <TerminateSubmissionDialog
          showTerminationConfirmation={terminationDialogState.isOpen}
          closeTerminationConfirmation={closeTerminationDialog}
          submission={submission}
          targetStatus={terminationDialogState.modalProps!.targetStatus}
          onUpdateSuccess={reFetchWorkspace}
        />
      )}
    </>
  );
}
